/**
 * The Ocean Problem Card and Company Card (the base layer, PRD §2 and §3) and
 * the right-side drawer (Solution / Company) that opens over them.
 *
 * Header, per the PRD: the base shows its badge, Save, Share and Close (no
 * Back — the base is not a drawer, MVP); the drawer shows Back (pops one
 * level; at the first level it returns to the base), a breadcrumb of the
 * exploration path (each step clickable), Save, Share and Close (back to the
 * base). Only one drawer exists; opening a card from the base starts it over,
 * opening one from inside the drawer stacks it. A COSMOS technology opens the
 * Cosmos technology card (App's modal), not a drawer of OCEAN's own.
 *
 * Each list on a card shows at most CARD_LIST_MAX items (the company's own
 * problem statements excepted); Share is the Cosmos share menu (Copy link,
 * WhatsApp, LinkedIn — tracked links, kinds ocean_problem / ocean_company).
 */
import React, { useEffect, useState } from 'react';
import { Icon } from './icons';
import { oc } from './api';
import type { CompanyCardData, CosmosState, Kind, ProblemCardData, Res, Scope, ScopeMode, SolutionCardData, SolutionsSource, SourceInfo, TechMini } from './api';
import { useOcean } from './context';
import type { CardRef } from './context';
import { CARD_LIST_MAX, CompanyLinks, KIND_LABEL, ProblemLinks, SolutionLinks, fmt, sourceAge } from './ui';
import { COLLECTIONS_ENABLED } from './flags';
import { ocTrack } from './track';
import { ShareMenu } from '../techcard/ShareMenu';
import { openSource } from '../common/SourcePane';
import type { ShareTargetKind } from '../techcard/api';

// ---------------------------------------------------------------- data
const CACHE_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; data: unknown }>();
const keyOf = (r: CardRef, scope: Scope | null) => `${r.type}:${r.id}:${r.ctx?.problem || ''}:${r.ctx?.q || ''}:${scope || ''}`;

const inflight = new Map<string, Promise<Res<unknown>>>();

/** One request per card at a time: the header (share title) and the body read the same card. */
function fetchCard(r: CardRef, scope: Scope | null): Promise<Res<unknown>> {
  const k = keyOf(r, scope);
  let p = inflight.get(k);
  if (!p) {
    p = loadCard(r, scope).finally(() => inflight.delete(k));
    inflight.set(k, p);
  }
  return p;
}

async function loadCard(r: CardRef, scope: Scope | null): Promise<Res<unknown>> {
  if (r.type === 'problem') {
    const res = await oc.problem(r.id, scope, r.ctx?.q);
    return { ...res, data: res.data?.problem ?? null };
  }
  if (r.type === 'company') {
    const res = await oc.company(r.id, r.ctx, scope);
    return { ...res, data: res.data?.company ?? null };
  }
  const res = await oc.solution(r.id);
  return { ...res, data: res.data?.solution ?? null };
}

function useCard<T>(r: CardRef): { data: T | null; error: string; retry: () => void } {
  const { scope } = useOcean();
  const k = keyOf(r, r.type === 'solution' ? null : scope);
  const [state, setState] = useState<{ k: string; data: T | null; error: string }>({ k: '', data: null, error: '' });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const hit = cache.get(k);
    if (hit && Date.now() - hit.at < CACHE_MS) { setState({ k, data: hit.data as T, error: '' }); return; }
    setState({ k, data: null, error: '' });
    let live = true;
    fetchCard(r, r.type === 'solution' ? null : scope).then(res => {
      if (!live) return;
      if (res.ok && res.data) { cache.set(k, { at: Date.now(), data: res.data }); setState({ k, data: res.data as T, error: '' }); }
      else setState({ k, data: null, error: res.error || 'Could not load this card.' });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, nonce]);
  return { data: state.k === k ? state.data : null, error: state.k === k ? state.error : '', retry: () => { cache.delete(k); setNonce(n => n + 1); } };
}

/** Update a cached card in place (the problem card once its semantic matches land). */
function patchCard(r: CardRef, scope: Scope | null, patch: Record<string, unknown>): void {
  const hit = cache.get(keyOf(r, scope));
  if (hit) cache.set(keyOf(r, scope), { at: hit.at, data: { ...(hit.data as object), ...patch } });
}

// ---------------------------------------------------------------- semantic COSMOS matches
const WAITING = ['none', 'pending', 'running'];
const POLL_MS = 4000;
const POLL_MAX = 45;   // 3 minutes; a job takes ~20 s once it starts

interface CosmosLive { solutions: TechMini[]; source: SolutionsSource; cosmos: CosmosState }

/** While the statement waits for For You's engine, ask every few seconds and
 *  swap the keyword list for the scored one when it lands. */
function useCosmos(p: ProblemCardData | null, r: CardRef): CosmosLive | null {
  const { scope } = useOcean();
  const [live, setLive] = useState<CosmosLive | null>(null);
  useEffect(() => {
    setLive(null);
    if (!p || p.solutions_source === 'semantic' || !WAITING.includes(p.cosmos?.status)) return;
    let alive = true;
    let n = 0;
    let timer = 0;
    const tick = async () => {
      const res = await oc.cosmos(p.id, p.scope);
      if (!alive) return;
      if (res.ok && res.data) {
        const d = res.data;
        const next = { solutions: d.solutions ?? p.solutions, source: d.solutions_source, cosmos: d.cosmos };
        setLive(next);
        if (d.solutions_source === 'semantic' || !WAITING.includes(d.cosmos.status)) {
          patchCard(r, scope, { solutions: next.solutions, solutions_source: next.source, cosmos: next.cosmos });
          return;
        }
      }
      if (++n < POLL_MAX) timer = window.setTimeout(tick, POLL_MS);
    };
    timer = window.setTimeout(tick, POLL_MS);
    return () => { alive = false; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.id, p?.solutions_source, p?.scope]);
  return live;
}

/** While a problem's technologies are being scored: a small pulsing dot by the
 *  heading, no words (no meta text on the card), named for screen readers. */
const Pending: React.FC<{ on: boolean }> = ({ on }) =>
  on ? <span className="oc-pending" role="status" aria-label="Finding more relevant technologies" /> : null;

const Loading: React.FC = () => (
  <div className="oc-loading" aria-busy="true" aria-label="Loading">
    <i style={{ width: '70%', height: 24 }} /><i style={{ width: '45%' }} /><i style={{ width: '55%' }} /><i style={{ width: '35%' }} />
  </div>
);

const LoadError: React.FC<{ error: string; retry: () => void }> = ({ error, retry }) => (
  <div className="oc-error" role="alert">{error}<button type="button" className="oc-link" onClick={retry}>Try again</button></div>
);

// ---------------------------------------------------------------- header
export interface Crumb { label: string; onClick?: () => void }

const Crumbs: React.FC<{ items: Crumb[] }> = ({ items }) => (
  <nav className="oc-crumbs" aria-label="Exploration path">
    {items.map((c, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span aria-hidden="true">›</span>}
        {i === items.length - 1 || !c.onClick
          ? <span aria-current={i === items.length - 1 ? 'location' : undefined} title={c.label}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{c.label}</span>
          : <button type="button" onClick={c.onClick} title={c.label}>{c.label}</button>}
      </React.Fragment>
    ))}
  </nav>
);

const SHARE_KIND: Record<Kind, ShareTargetKind> = { problem: 'ocean_problem', company: 'ocean_company', solution: 'tech' };
const shareTrack = (action: string, props: Record<string, unknown>) => ocTrack(action.replace(/^techcard_/, 'ocean_'), props);

/** What a shared link is called (WhatsApp puts it before the URL). */
const shareTitle = (r: CardRef, d: unknown): string => {
  const x = (d || {}) as { statement?: string; name?: string; title?: string };
  return (r.type === 'problem' ? x.statement : r.type === 'company' ? x.name : x.title) || r.label || 'Ocean';
};

const Actions: React.FC<{ r: CardRef; onClose: () => void; closeLabel: string }> = ({ r, onClose, closeLabel }) => {
  const o = useOcean();
  const { type: kind, id } = r;
  const { data } = useCard<unknown>(r);
  const title = shareTitle(r, data);
  const saved = o.savedOf(kind, id);
  const saveTitle = saved
    ? (COLLECTIONS_ENABLED ? `Saved in ${saved.collection || 'a collection'} — move or remove` : 'Saved — click to remove')
    : (COLLECTIONS_ENABLED ? `Save ${KIND_LABEL[kind]} Card to a collection` : `Save ${KIND_LABEL[kind]} Card`);
  return (
    <div className="oc-actions">
      {/* The collection picker: existing collections, a new one by name, and Remove once saved. */}
      <button type="button" className="oc-act" aria-pressed={!!saved} title={saveTitle} aria-label={saveTitle}
              onClick={() => o.chooseCollection(kind, id, 'card')}>
        <Icon name={saved ? 'bookmarkFilled' : 'bookmark'} size={16} />
        <span className="oc-act-label">{saved ? (COLLECTIONS_ENABLED ? (saved.collection || 'Saved') : 'Saved') : 'Save'}</span>
      </button>
      <ShareMenu target={{ kind: SHARE_KIND[kind], id }} title={title} surface={`ocean_${kind}`}
                 onToast={o.toast} track={shareTrack} label={`Share this ${KIND_LABEL[kind].toLowerCase()} card`}
                 buttonClassName="oc-act"
                 buttonContent={<><Icon name="share" size={16} /><span className="oc-act-label">Share</span></>} />
      <button type="button" className="oc-close" title={closeLabel} aria-label={closeLabel} onClick={onClose}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------- bodies
const host = (u: string): string => {
  try { const x = new URL(u); const p = x.pathname.length > 40 ? x.pathname.slice(0, 39) + '…' : x.pathname; return x.host + (p === '/' ? '' : p); }
  catch { return u; }
};

const ProblemBody: React.FC<{ r: CardRef; inDrawer?: boolean }> = ({ r }) => {
  const o = useOcean();
  const { data: p, error, retry } = useCard<ProblemCardData>(r);
  const [sourceOpen, setSourceOpen] = useState(false);
  useEffect(() => setSourceOpen(false), [r.id]);
  const live = useCosmos(p, r);
  if (error) return <div className="oc-body"><LoadError error={error} retry={retry} /></div>;
  if (!p) return <div className="oc-body"><Loading /></div>;
  const age = sourceAge(p.source, o.floorLabel);
  const extra = p.source_count - p.sources.length;
  const cosmos = live?.cosmos ?? p.cosmos;
  const scoring = (live?.source ?? p.solutions_source) !== 'semantic' && !!cosmos && WAITING.includes(cosmos.status)
    && cosmos.note !== 'cap';
  const openSrc = (s: SourceInfo, i: number, link: 'cta' | 'source_row') => openSource({
    url: s.url || '', title: `${p.company.name} · ${s.type_label}`, surface: 'ocean_problem', track: ocTrack,
    props: { card_id: p.id, card_kind: 'problem', source_type: s.type, position: i, link },
  });
  return (
    <div className="oc-body">
      <h2 className="oc-big">{p.statement}</h2>
      <div className="oc-facts">
        <dl className="oc-dl">
          <div><dt>Organisation</dt><dd>
            <button type="button" className="oc-org" onClick={() => o.openFromBase({ type: 'company', id: p.company.id, label: p.company.name, ctx: { problem: p.id } })}>
              {p.company.name}
            </button>
          </dd></div>
          <div><dt>Industry</dt><dd>{p.industry || 'Not stated'}</dd></div>
          <div><dt>Country</dt><dd>{p.country || 'Not stated'}</dd></div>
          <div><dt>Source</dt><dd title={age.title}>{age.text} · {p.source.type_label}</dd></div>
        </dl>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div className="oc-label" style={{ color: 'var(--gray-300)', marginBottom: 8 }}>Technical tags</div>
          <div className="oc-chips">
            {p.tags.map(t => <span key={t} className="oc-tag" style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t}</span>)}
          </div>
          {p.goals.length > 0 && <div className="oc-muted" style={{ marginTop: 10 }}>Goals: {p.goals.join(' · ')}</div>}
        </div>
      </div>

      {/* The source opens in the in-app source pane (components/common/SourcePane);
          the evidence list stays below it, each page's link opening the pane too. */}
      <button type="button" className="oc-cta" title="Access the original source for full details" aria-expanded={sourceOpen}
              onClick={() => {
                setSourceOpen(v => !v);
                if (sourceOpen) return;
                ocTrack('ocean_source_open', { type: p.source.type });
                const first = p.sources.findIndex(s => !!s.url);
                if (first >= 0) openSrc(p.sources[first], first, 'cta');
              }}>
        {sourceOpen ? 'Hide original source' : 'View original source'}
      </button>
      {sourceOpen && (
        <div>
          {p.sources.map((s, i) => {
            const a = sourceAge(s, o.floorLabel);
            return (
              <div key={i} className="oc-evidence">
                <div className="oc-label" style={{ color: 'var(--primary-700)', fontSize: 12, marginBottom: 6 }}>
                  {i === 0 ? 'Problem evidence' : 'Also stated in'}
                </div>
                {s.evidence ? <q>{s.evidence}</q> : <div className="oc-hsub">No excerpt was kept for this page.</div>}
                <div className="oc-meta">
                  <span>Source type: {s.type_label}</span>
                  <span title={a.title}>{a.text}</span>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noopener noreferrer nofollow"
                       onClick={e => {
                         // A modified click (new tab / window) keeps the browser's own behaviour.
                         if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                         e.preventDefault();
                         openSrc(s, i, 'source_row');
                       }}>{host(s.url)} ↗</a>
                  )}
                </div>
              </div>
            );
          })}
          {extra > 0 && <div className="oc-hsub" style={{ marginTop: 8 }}>…and {fmt(extra)} more page{extra === 1 ? '' : 's'} state the same problem.</div>}
        </div>
      )}

      <ScopeToggle industry={p.industry} scope={p.scope} mode={p.scope_mode} />
      <div className="oc-tri">
        <section className="oc-box3" aria-busy={scoring}>
          <h3>Relevant IPs found in COSMOS<Pending on={scoring} /></h3>
          <SolutionLinks items={live?.solutions ?? p.solutions} max={CARD_LIST_MAX}
                         empty="No close match in COSMOS yet — possibly white space."
                         onOpen={(id, label, rel) => o.openTech(id, rel, label, 'base')} />
        </section>
        <section className="oc-box3"><h3>Companies with similar problem statements</h3>
          <CompanyLinks items={p.similar_companies} max={CARD_LIST_MAX} empty="No other company states a close match."
                        onOpen={(id, label) => o.openFromBase({ type: 'company', id, label, ctx: { problem: p.id } })} />
        </section>
        <section className="oc-box3"><h3>Related problem statements</h3>
          <ProblemLinks items={p.related} max={CARD_LIST_MAX} empty="No related problem statements yet."
                        onOpen={id => o.openProblem(id, 'related')} />
        </section>
      </div>
    </div>
  );
};

/** "Matches within: Same industry | All industries" — the card's three lists
 *  (the choice holds for every card until switched back). */
const ScopeToggle: React.FC<{ industry: string | null; scope: Scope; mode?: ScopeMode }> = ({ industry, scope, mode }) => {
  const o = useOcean();
  // Admin -> Ocean -> Search engine can show one set only (card_scope_mode): then no switch.
  if (!industry || (mode && mode !== 'both')) return null;
  const pick = (s: Scope) => { if (s !== scope) { ocTrack('ocean_scope', { scope: s }); o.setScope(s); } };
  return (
    <div className="oc-scope" role="radiogroup" aria-label="Show matches within">
      <span className="oc-muted">Matches within</span>
      <button type="button" role="radio" aria-checked={scope === 'industry'} className={scope === 'industry' ? 'on' : ''}
              onClick={() => pick('industry')} title={industry}>Same industry</button>
      <button type="button" role="radio" aria-checked={scope === 'all'} className={scope === 'all' ? 'on' : ''}
              onClick={() => pick('all')}>All industries</button>
    </div>
  );
};

const COMPANY_SHOWN = 8;

const CompanyBody: React.FC<{ r: CardRef; inDrawer?: boolean }> = ({ r, inDrawer }) => {
  const o = useOcean();
  const { data: c, error, retry } = useCard<CompanyCardData>(r);
  const [all, setAll] = useState(false);
  useEffect(() => setAll(false), [r.id]);
  const wrap = inDrawer ? undefined : 'oc-body';
  if (error) return <div className={wrap}><LoadError error={error} retry={retry} /></div>;
  if (!c) return <div className={wrap}><Loading /></div>;
  const openCompany = (id: string, label: string) =>
    inDrawer ? o.pushDrawer({ type: 'company', id, label }) : o.openFromBase({ type: 'company', id, label });
  const problems = all ? c.problems : c.problems.slice(0, COMPANY_SHOWN);
  const basis = c.relevance_basis === 'problem'
    ? 'How closely this company’s best-matching problem statement matches the problem you came from.'
    : 'How this company’s best match compares with the top result for your search.';
  const lists = [
    <React.Fragment key="p">
      <ProblemLinks items={problems} showCompany={false} bordered={inDrawer} empty="No problem statements."
                    onOpen={id => o.openProblem(id, inDrawer ? 'company_drawer' : 'company_card')} />
      {c.problems.length > COMPANY_SHOWN && (
        <button type="button" className="oc-link" style={{ margin: '4px 10px 8px' }} onClick={() => setAll(v => !v)}>
          {all ? 'Show fewer' : c.problem_count && c.problem_count > c.problems.length
            ? `Show ${fmt(c.problems.length)} of ${fmt(c.problem_count)}` : `Show all ${fmt(c.problems.length)}`}
        </button>
      )}
      {all && c.problem_count && c.problem_count > c.problems.length && (
        <div className="oc-none">The {fmt(c.problems.length)} most-cited of {fmt(c.problem_count)}.</div>
      )}
    </React.Fragment>,
    <SolutionLinks key="s" items={c.solutions} bordered={inDrawer} max={CARD_LIST_MAX} empty="No close match in COSMOS yet."
                   onOpen={(id, label, rel) => o.openTech(id, rel, label, inDrawer ? 'drawer' : 'base')} />,
    <CompanyLinks key="c" items={c.similar_companies} bordered={inDrawer} max={CARD_LIST_MAX} empty="No similar companies found."
                  onOpen={openCompany} />,
  ];
  const titles = ['Company problem statements', 'Relevant IPs found in COSMOS', 'Companies with similar problem statements'];
  return (
    <div className={wrap}>
      {c.relevance != null && <span className="oc-relpill" title={basis}>Relevance: {c.relevance}%</span>}
      <h2 className={inDrawer ? 'oc-dtitle' : 'oc-big'} style={{ marginTop: c.relevance != null ? 12 : 0 }}>{c.name}</h2>
      <dl className="oc-dl" style={{ marginTop: 14 }}>
        <div><dt>Industry</dt><dd>{c.industry || 'Not stated'}</dd></div>
        <div><dt>Country</dt><dd>{c.country || 'Not stated'}</dd></div>
        <div><dt>Problems</dt><dd>{fmt(c.problem_count)} problem statement{c.problem_count === 1 ? '' : 's'}</dd></div>
      </dl>
      <ScopeToggle industry={c.industry} scope={c.scope} mode={c.scope_mode} />
      {inDrawer ? titles.map((t, i) => (
        <React.Fragment key={t}><div className="oc-dsec">{t}</div>{lists[i]}</React.Fragment>
      )) : (
        <div className="oc-tri">
          {titles.map((t, i) => <section key={t} className="oc-box3"><h3>{t}</h3>{lists[i]}</section>)}
        </div>
      )}
    </div>
  );
};

const SolutionBody: React.FC<{ r: CardRef }> = ({ r }) => {
  const o = useOcean();
  const { data: s, error, retry } = useCard<SolutionCardData>(r);
  const [tab, setTab] = useState<'summary' | 'ip' | 'pubs'>('summary');
  const [trlOpen, setTrlOpen] = useState(false);
  useEffect(() => { setTab('summary'); setTrlOpen(false); }, [r.id]);
  if (error) return <LoadError error={error} retry={retry} />;
  if (!s) return <Loading />;
  const tabs: [typeof tab, string][] = [['summary', 'Summary'], ['ip', 'IP Highlights'], ['pubs', 'Publications']];
  return (
    <div>
      <span className="oc-found">Found in COSMOS</span>
      <h2 className="oc-dtitle">{s.title}</h2>
      <div className="oc-place">
        <span><Icon name="location" size={14} />{s.country || 'Country not stated'}</span>
        {s.university && <span>{s.university}</span>}
      </div>
      <div className="oc-trl">
        <button type="button" aria-expanded={trlOpen} disabled={!s.trl_statement} onClick={() => setTrlOpen(v => !v)}
                style={{ cursor: s.trl_statement ? 'pointer' : 'default' }}>
          <span>{s.trl ? `TRL ${s.trl}` : 'TRL not disclosed'}</span>
          {s.trl_statement && <Icon name={trlOpen ? 'chevronUp' : 'chevronDown'} size={16} />}
        </button>
        {trlOpen && s.trl_statement && <p>{s.trl_statement}</p>}
      </div>
      <div className="oc-seg" role="tablist" aria-label="Technology details">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} id={`oc-tab-${k}`} aria-controls="oc-tabpanel"
                  onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      <div className="oc-tabpanel" role="tabpanel" id="oc-tabpanel" aria-labelledby={`oc-tab-${tab}`}>
        {tab === 'summary' && (s.summary || 'No summary available.')}
        {tab === 'ip' && (
          s.ip.summary || s.ip.patents.length ? <>
            {s.ip.summary && <div>{s.ip.summary}</div>}
            {s.ip.patents.length > 0 && <ul>{s.ip.patents.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </> : 'No IP details disclosed.'
        )}
        {tab === 'pubs' && (
          s.publications.summary || s.publications.items.length ? <>
            {s.publications.summary && <div>{s.publications.summary}</div>}
            {s.publications.items.length > 0 && <ul>{s.publications.items.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </> : 'No publications listed.'
        )}
      </div>
      {s.cosmos_path && (
        <a className="oc-link" style={{ display: 'inline-block', marginTop: 14 }} href={s.cosmos_path} target="_blank" rel="noopener noreferrer">
          Open the full technology page in COSMOS ↗
        </a>
      )}
      <div className="oc-dsec">Companies with similar problem statements</div>
      <CompanyLinks items={s.similar_companies} bordered empty="No company states a close match."
                    onOpen={(id, label) => o.pushDrawer({ type: 'company', id, label })} />
      <div className="oc-dsec">Related problem statements</div>
      <ProblemLinks items={s.related} bordered empty="No related problem statements yet." onOpen={id => o.openProblem(id, 'solution_drawer')} />
    </div>
  );
};

// ---------------------------------------------------------------- shells
export const BaseCard: React.FC<{ r: CardRef; crumbs: Crumb[]; drawerOpen: boolean; onClose: () => void; onBackdrop: () => void }> =
  ({ r, crumbs, drawerOpen, onClose, onBackdrop }) => (
    <div className="oc-overlay" onClick={onBackdrop}>
      <div className="oc-base" role="dialog" aria-modal={!drawerOpen} aria-label={r.type === 'problem' ? 'Ocean problem card' : 'Ocean company card'}
           onClick={e => e.stopPropagation()}>
        <div className="oc-bar">
          <div className="oc-row" style={{ minWidth: 0, flexWrap: 'wrap' }}>
            <span className="oc-badge">{r.type === 'problem' ? 'Ocean problem card' : 'Ocean company card'}</span>
            {crumbs.length > 1 && <Crumbs items={crumbs} />}
          </div>
          <Actions r={r} onClose={onClose} closeLabel="Close" />
        </div>
        {r.type === 'problem' ? <ProblemBody r={r} /> : <CompanyBody r={r} />}
      </div>
    </div>
  );

export const Drawer: React.FC<{ r: CardRef; crumbs: Crumb[]; onBack: () => void; onClose: () => void; backLabel: string }> =
  ({ r, crumbs, onBack, onClose, backLabel }) => (
    <aside className="oc-drawer" role="dialog" aria-modal="true" aria-label={r.type === 'solution' ? 'COSMOS solution' : 'Company'}>
      <div className="oc-bar" style={{ borderBottom: 0, paddingBottom: 8 }}>
        <button type="button" className="oc-act" onClick={onBack} title={backLabel} aria-label={backLabel}>
          <Icon name="chevronLeft" size={14} />Back
        </button>
        <Actions r={r} onClose={onClose} closeLabel="Close the drawer" />
      </div>
      <div className="oc-crumbbar"><Crumbs items={crumbs} /></div>
      <div className="oc-drawer-body">
        {r.type === 'solution' ? <SolutionBody r={r} /> : <CompanyBody r={r} inDrawer />}
      </div>
    </aside>
  );
